import apiClient from './apiClient';

/**
 * Debug Console Service
 * Fetches PM2 logs, database tables, and system info from the backend
 */
const debugService = {
    /**
     * Get PM2 log output
     * @param {Object} params - { lines, type, search }
     */
    async getLogs(params = {}) {
        const query = new URLSearchParams();
        if (params.lines) query.set('lines', params.lines);
        if (params.type) query.set('type', params.type);
        if (params.search) query.set('search', params.search);
        const qs = query.toString();
        return apiClient.get(`/debug/logs${qs ? `?${qs}` : ''}`);
    },

    /**
     * Get list of database tables
     */
    async getDatabaseTables() {
        return apiClient.get('/debug/database/tables');
    },

    /**
     * Get data from a specific table
     * @param {string} tableName
     * @param {Object} params - { page, limit, orderBy, order }
     */
    async getTableData(tableName, params = {}) {
        const query = new URLSearchParams();
        if (params.page) query.set('page', params.page);
        if (params.limit) query.set('limit', params.limit);
        if (params.orderBy) query.set('orderBy', params.orderBy);
        if (params.order) query.set('order', params.order);
        const qs = query.toString();
        return apiClient.get(`/debug/database/table/${tableName}${qs ? `?${qs}` : ''}`);
    },

    /**
     * Execute a read-only SQL query
     * @param {string} query - SQL SELECT query
     */
    async executeQuery(query) {
        return apiClient.post('/debug/database/query', { query });
    },

    /**
     * Get system info (memory, uptime, etc.)
     */
    async getSystemInfo() {
        return apiClient.get('/debug/system');
    },

    /**
     * Create an EventSource connection for real-time log streaming
     * @param {function} onMessage - callback for each log message
     * @param {function} onError - callback for errors
     * @returns {EventSource}
     */
    createLogStream(onMessage, onError) {
        // Same default as api/client.js — EventSource can't go through axios.
        const API_BASE = process.env.REACT_APP_API_URL || 'https://api.mtandtiot.in/api';
        const eventSource = new EventSource(`${API_BASE}/debug/logs/stream`);

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (onMessage) onMessage(data);
            } catch (e) {
                console.error('Error parsing SSE data:', e);
            }
        };

        eventSource.onerror = (err) => {
            console.error('SSE connection error:', err);
            if (onError) onError(err);
        };

        return eventSource;
    },

    /**
     * Get recent Autopeepal collector logs
     */
    async getAutopeepalLogs(lines = 100) {
        return apiClient.get(`/debug/autopeepal/logs?lines=${lines}`);
    },

    /**
     * Create an EventSource for Autopeepal real-time stream
     */
    createAutopeepalStream(onMessage, onError) {
        // Same default as api/client.js (the old fallback hit the website
        // domain, which 404s and has no CORS headers).
        const API_BASE = process.env.REACT_APP_API_URL || 'https://api.mtandtiot.in/api';
        const eventSource = new EventSource(`${API_BASE}/debug/autopeepal/stream`);

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (onMessage) onMessage(data);
            } catch (e) {
                console.error('Error parsing Autopeepal SSE data:', e);
            }
        };

        eventSource.onerror = (err) => {
            console.error('Autopeepal SSE connection error:', err);
            if (onError) onError(err);
        };

        return eventSource;
    }
};

export default debugService;
